import collect from 'collect.js';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { uiHelpers } from '../../../helpers';
import { Tip } from '../../../shared/types';
import { PriceDataPoint } from '../../../types';
import {
  ExternalMarkerGenerator,
  ExternalMarkerGeneratorGetMarkers,
} from './base';

interface TipsTooltipProps {
  tips: Tip[];
  direction: string;
}
const TipsTooltip: React.FC<TipsTooltipProps> = ({ tips, direction }) => {
  const [t] = useTranslation();
  const fallbackComment = t(`expert-${direction}`);
  const { i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en' : 'zh-hk';

  return (
    <div>
      {tips.map((tip, i) => (
        <div key={i}>
          <div>
            <b>{tip.name}</b>
          </div>
          <div>
            {uiHelpers.formatDateTimeLocal(
              DateTime.fromISO(tip.date).setLocale(locale).toString()
            )}{' '}
            ({t('local-time')})
          </div>
          <div>{tip.keyComment || fallbackComment}</div>
        </div>
      ))}
    </div>
  );
};

const bullishColor = '#82A3FF';
const bearishColor = '#EFBE7D';

const makeData = (
  direction: string,
  date: string,
  dataPoint: PriceDataPoint,
  width: number
) => {
  const bearish = direction === 'bearish';
  return {
    name: direction,
    coord: [date, bearish ? dataPoint.high : dataPoint.low],
    symbol: 'arrow',
    symbolSize: [width, width],
    symbolRotate: bearish ? 180 : 0,
    symbolOffset: [0, bearish ? '-100%' : '100%'],
    itemStyle: {
      color: bearish ? bearishColor : bullishColor,
    },
  };
};

class Tips extends ExternalMarkerGenerator {
  tips: Tip[];
  constructor(tips: Tip[]) {
    super();
    this.tips = tips || [];
  }

  getMarkers(
    dataPoints: ReadonlyArray<PriceDataPoint>,
    markPointWidth: number
  ): ExternalMarkerGeneratorGetMarkers {
    const tipsWithCoordDate = this.tips
      .filter(tip => tip.priceDate)
      .map(tip => ({
        ...tip,
        // tips' price dates are zoned market time in string, no need conversion
        coordDate: tip.priceDate.split('T')[0],
      }));

    const tipsByDate = collect(tipsWithCoordDate).groupBy('coordDate').all();
    const data = [];
    for (const dataPoint of dataPoints) {
      const date = String(dataPoint.date);
      const tipsOnDate: Tip[] = tipsByDate[date]?.items;
      if (tipsOnDate) {
        const directions = new Set(
          tipsOnDate.map(({ direction }) => direction)
        );
        for (const direction of directions) {
          data.push(makeData(direction, date, dataPoint, markPointWidth));
        }
      }
    }

    const tooltipCreater = (
      name: string, // direction
      coord: [string, number]
    ): React.ReactElement => {
      const [date] = coord;
      const tipsOnDate: Tip[] = tipsByDate[date]?.items;
      const tipsMatchingDirection = tipsOnDate.filter(
        ({ direction }) => direction === name
      );
      return React.createElement(TipsTooltip, {
        tips: tipsMatchingDirection,
        direction: name,
      });
    };
    return { data, tooltipCreater };
  }
}

export default Tips;
